// Third Party
import * as React from 'react';

// Utils
import { isSSR } from '@utils/predicates';
import moment from 'moment';
import {
  ListingBookingInfoQuery,
  useListingBookingInfoQuery,
  BookingQuoteByListingAndDatesQuery,
  useBookingQuoteByListingAndDatesLazyQuery,
} from '@gql';
import { formatDateYYYYMMDD } from '@utils/date-helpers';
import { useToggle } from '@hooks';
import { isInclusivelyAfterDay } from 'react-dates';
import { guestDescription } from '@utils/guest-description';
import { useAnalytics } from '@contexts/use-analytics';

export type AvailabilityHash = Record<
  string,
  {
    date: string;
    available: boolean;
    checkInAllowed: boolean;
    checkOutAllowed: boolean;
    minNights?: number;
    maxNights?: number;
  }
>;

export type QuotesContext = {
  /** Activate the mobile quote modal */
  activateQuotesModal: VoidFunction;
  /** Deactivate the mobile quote modal */
  deactivateQuotesModal: VoidFunction;
  /** Flag indicating whether or not the mobile feature modal is active */
  quotesModalActive: boolean;
  /** Adult count for the quote */
  adultCount: number;
  /** Set the adult count for the quote */
  setAdultCount: React.Dispatch<React.SetStateAction<number>>;
  /** Child count for the quote */
  childCount: number;
  /** Set the child count for the quote */
  setChildCount: React.Dispatch<React.SetStateAction<number>>;
  /** Infant count for the quote */
  infantCount: number;
  /** Set the infant count for the quote */
  setInfantCount: React.Dispatch<React.SetStateAction<number>>;
  /** Pet count for the quote */
  petCount: number;
  /** Set the pet count for the quote */
  setPetCount: React.Dispatch<React.SetStateAction<number>>;
  /** Human readable guest count */
  guestCountHumanReadableValue: string;
  /** The listing's guest capacity */
  guestCapacity: number | undefined;
  /** Flag indicating whether or not the listing's capacity has been reached */
  guestCapacityReached: boolean;
  /** Check in date for the quote */
  checkInDate: string | null;
  /** Set the check in date for the quote */
  setCheckInDate: React.Dispatch<React.SetStateAction<string | null>>;
  /** Check out date for the quote */
  checkOutDate: string | null;
  /** Set the check out date for the quote */
  setCheckOutDate: React.Dispatch<React.SetStateAction<string | null>>;
  /** Quote results data */
  quoteData?: BookingQuoteByListingAndDatesQuery;
  /** Quote search data loading flag */
  quoteDataLoading: boolean;
  /** Calculate the service fee savings for a given quote */
  calculateServiceFeeSavingsCents: (totalWithoutTaxesCents: number) => number;
  /** Boolean indicating whether or not dates are present */
  datesPresent: boolean;
  /** Boolean indicating whether or not dates are invalid */
  datesInvalid: boolean;
  /** Number of nights between selected check in and check out dates */
  numberOfNights: number;
  /** Boolean indicating whether or not dates are present */
  bookingQuotePresent: boolean;
  /** Availability results data */
  availabilityData?: ListingBookingInfoQuery;
  /** Availability data loading flag */
  availabilityDataLoading: boolean;
  /** Boolean to indicate whether or not day is blocked */
  isDayBlocked: (date: moment.Moment) => boolean;
  /** Boolean to indicate whether or not day the start day is outside of range */
  isStartDayBlocked: (date: moment.Moment) => boolean;
  /** Boolean to indicate whether or not day the end day is outside of range */
  isEndDayBlocked: (date: moment.Moment) => boolean;
  /** Minimum number of nights for selected date */
  minimumNightsForSelectedDate: number;
  /** Get the minimum number of nights for a given date */
  getMinNightsForHoverDate: (date: moment.Moment) => number;
};

const QuotesContext = React.createContext<QuotesContext>(undefined!);

type QuotesProviderProps = {
  children: React.ReactNode;
  listingSlug: string;
  guestCapacity?: number;
  adultCount: number | null;
  childCount: number | null;
  infantCount: number | null;
  petCount: number | null;
  checkInDate: string | null;
  checkOutDate: string | null;
};

export const QuotesProvider: React.FC<QuotesProviderProps> = ({
  children,
  listingSlug,
  guestCapacity,
  adultCount: _adultCount = 1,
  childCount: _childCount = 0,
  infantCount: _infantCount = 0,
  petCount: _petCount = 0,
  checkInDate: _checkInDate,
  checkOutDate: _checkOutDate,
}) => {
  const {
    on: quotesModalActive,
    setOn: activateQuotesModal,
    setOff: deactivateQuotesModal,
  } = useToggle(false);

  const { data: availabilityData, loading: availabilityDataLoading } =
    useListingBookingInfoQuery({
      ssr: isSSR,
      variables: {
        slug: listingSlug,
      },
    });

  const { emitViewQuoteEvent } = useAnalytics();

  const currentMoment = React.useMemo(moment, []);

  const [availabilityDates, setAvailabilityDates] =
    React.useState<AvailabilityHash>({});

  /**
   * Update the unavailable dates and minimum night stays when the availability data changes.
   */
  React.useEffect(() => {
    if (
      availabilityData?.listingBookingInfo &&
      availabilityData?.listingBookingInfo.__typename == 'ListingBookingInfo'
    ) {
      const availabilityDates =
        availabilityData.listingBookingInfo.availabilityDates;
      const hash: AvailabilityHash = {};
      availabilityDates.forEach((item) => {
        hash[item.date] = item;
      });

      setAvailabilityDates(hash);
    }
  }, [JSON.stringify(availabilityData)]);

  const [adultCount, setAdultCount] = React.useState(_adultCount || 1);

  const [childCount, setChildCount] = React.useState(_childCount || 0);

  const [infantCount, setInfantCount] = React.useState(_infantCount || 0);

  const [petCount, setPetCount] = React.useState(_petCount || 0);

  const [guestCountHumanReadableValue, setGuestCountHumanReadableValue] =
    React.useState('');

  const [guestCapacityReached, setGuestCapacityReached] =
    React.useState<boolean>(false);

  const [checkInDate, setCheckInDate] = React.useState<string | null>(
    _checkInDate && _checkOutDate ? _checkInDate : null
  );
  const [checkOutDate, setCheckOutDate] = React.useState<string | null>(
    _checkInDate && _checkOutDate ? _checkOutDate : null
  );

  const [nextUnavailableDate, setNextUnavailableDate] = React.useState<
    string | null
  >(null);

  const [minimumNightsForSelectedDate, setMinimumNightsForSelectedDate] =
    React.useState<number>(1);

  // Update the URL query params when the adult count changes
  React.useEffect(() => {
    // Construct URLSearchParams object instance from current URL querystring.
    var queryParams = new URLSearchParams(window.location.search);
    queryParams.set('adultCount', adultCount.toString());
    // Replace current querystring with the new one.
    history.replaceState(null, '', '?' + queryParams.toString());
  }, [adultCount]);

  // Update the URL query params when the child count changes
  React.useEffect(() => {
    // Construct URLSearchParams object instance from current URL querystring.
    var queryParams = new URLSearchParams(window.location.search);
    queryParams.set('childCount', childCount.toString());
    // Replace current querystring with the new one.
    history.replaceState(null, '', '?' + queryParams.toString());
  }, [childCount]);

  // Update the URL query params when the infant count changes
  React.useEffect(() => {
    // Construct URLSearchParams object instance from current URL querystring.
    var queryParams = new URLSearchParams(window.location.search);
    queryParams.set('infantCount', infantCount.toString());
    // Replace current querystring with the new one.
    history.replaceState(null, '', '?' + queryParams.toString());
  }, [infantCount]);

  // Update the URL query params when the pet count changes
  React.useEffect(() => {
    // Construct URLSearchParams object instance from current URL querystring.
    var queryParams = new URLSearchParams(window.location.search);
    queryParams.set('petCount', petCount.toString());
    // Replace current querystring with the new one.
    history.replaceState(null, '', '?' + queryParams.toString());
  }, [petCount]);

  // Update the guest count human readable value if the counts change
  React.useEffect(() => {
    const description = guestDescription(
      adultCount,
      childCount,
      infantCount,
      petCount
    );

    setGuestCountHumanReadableValue(description);
  }, [adultCount, childCount, infantCount, petCount]);

  // Update the guest capacity reached flag true if the adult and child counts exceed the listing's capacity
  React.useEffect(() => {
    if (guestCapacity && adultCount + childCount >= guestCapacity) {
      setGuestCapacityReached(true);
    } else {
      setGuestCapacityReached(false);
    }
  }, [adultCount, childCount]);

  React.useEffect(() => {
    const availability = checkInDate ? availabilityDates[checkInDate] : null;
    setMinimumNightsForSelectedDate(
      availability ? availability.minNights || 1 : 1
    );
  }, [
    checkInDate ? checkInDate.toString() : '',
    JSON.stringify(availabilityDates),
  ]);

  const [
    bookingQuoteByListingAndDatesLazyQuery,
    { data: quoteData, loading: quoteDataLoading },
  ] = useBookingQuoteByListingAndDatesLazyQuery({});

  React.useEffect(() => {
    if (checkInDate && checkOutDate) {
      bookingQuoteByListingAndDatesLazyQuery({
        variables: {
          slug: listingSlug,
          checkInDate,
          checkOutDate,
          adultCount,
          childCount,
          infantCount,
          petCount,
        },
        fetchPolicy: 'no-cache',
      });
    }
  }, [
    checkInDate,
    checkOutDate,
    adultCount,
    childCount,
    infantCount,
    petCount,
  ]);

  const [datesPresent, setDatesPresent] = React.useState<boolean>(
    checkInDate && checkOutDate ? true : false
  );

  const [datesInvalid, setDatesInvalid] = React.useState<boolean>(
    checkInDate && checkOutDate && checkInDate > checkOutDate ? true : false
  );

  // Update the dates present and dates valid flags when the check in and check out dates change
  React.useEffect(() => {
    if (checkInDate && checkOutDate) {
      setDatesPresent(true);
      if (checkInDate > checkOutDate) {
        setDatesInvalid(true);
      } else {
        setDatesInvalid(false);
      }
    } else {
      setDatesPresent(false);
    }
  }, [checkInDate, checkOutDate]);

  const [numberOfNights, setNumberOfNights] = React.useState<number>(
    datesPresent ? moment(checkOutDate).diff(moment(checkInDate), 'days') : 1
  );

  React.useEffect(() => {
    if (checkInDate && checkOutDate) {
      setNumberOfNights(moment(checkOutDate).diff(moment(checkInDate), 'days'));
    }
  }, [checkInDate, checkOutDate]);

  const [bookingQuotePresent, setBookingQuotePresent] = React.useState<boolean>(
    !quoteDataLoading &&
      quoteData &&
      quoteData.bookingQuoteByListingAndDates &&
      quoteData.bookingQuoteByListingAndDates.__typename == 'BookingQuote' &&
      quoteData.bookingQuoteByListingAndDates.totalCostCents
      ? true
      : false
  );
  // Update the booking quote present flag true if the booking quote query responds with a BookingQuote object
  React.useEffect(() => {
    if (
      !quoteDataLoading &&
      quoteData &&
      quoteData.bookingQuoteByListingAndDates &&
      quoteData.bookingQuoteByListingAndDates.__typename == 'BookingQuote' &&
      quoteData.bookingQuoteByListingAndDates.totalCostCents
    ) {
      setBookingQuotePresent(true);
    } else {
      setBookingQuotePresent(false);
    }
  }, [quoteData, quoteDataLoading]);

  const [costPerNight, setCostPerNight] = React.useState<number>(
    numberOfNights &&
      quoteData &&
      quoteData.bookingQuoteByListingAndDates &&
      quoteData.bookingQuoteByListingAndDates.__typename == 'BookingQuote' &&
      quoteData.bookingQuoteByListingAndDates.totalCostCents
      ? Math.round(
          quoteData.bookingQuoteByListingAndDates.totalCostCents /
            numberOfNights
        )
      : 100
  );

  const calculateServiceFeeSavingsCents = (
    totalWithoutTaxes: number
  ): number => {
    return totalWithoutTaxes * 0.15;
  };

  React.useEffect(() => {
    if (
      numberOfNights &&
      quoteData &&
      quoteData.bookingQuoteByListingAndDates &&
      quoteData.bookingQuoteByListingAndDates.__typename == 'BookingQuote' &&
      quoteData.bookingQuoteByListingAndDates.totalCostCents
    ) {
      setCostPerNight(
        Math.round(
          quoteData.bookingQuoteByListingAndDates.totalCostCents /
            numberOfNights
        )
      );
    }
  }, [costPerNight, numberOfNights, quoteData]);

  // Update the URL query params when the check in date changes
  React.useEffect(() => {
    if (checkInDate) {
      // Construct URLSearchParams object instance from current URL querystring.
      var queryParams = new URLSearchParams(window.location.search);
      queryParams.set('checkInDate', checkInDate);
      // Replace current querystring with the new one.
      history.replaceState(null, '', '?' + queryParams.toString());

      setNextUnavailableDate(
        getNextUnavailableDate(availabilityDates, checkInDate)
      );
    } else {
      // Construct URLSearchParams object instance from current URL querystring.
      var queryParams = new URLSearchParams(window.location.search);
      queryParams.delete('checkInDate');
      // Replace current querystring with the new one.
      history.replaceState(null, '', '?' + queryParams.toString());
      setNextUnavailableDate(formatDateYYYYMMDD(moment().add(1, 'year')));
    }
  }, [checkInDate]);

  // Update the URL query params when the check out date changes
  React.useEffect(() => {
    if (checkOutDate) {
      // Construct URLSearchParams object instance from current URL querystring.
      var queryParams = new URLSearchParams(window.location.search);
      queryParams.set('checkOutDate', checkOutDate);
      // Replace current querystring with the new one.
      history.replaceState(null, '', '?' + queryParams.toString());
    } else {
      // Construct URLSearchParams object instance from current URL querystring.
      var queryParams = new URLSearchParams(window.location.search);
      queryParams.delete('checkOutDate');
      // Replace current querystring with the new one.
      history.replaceState(null, '', '?' + queryParams.toString());
    }
  }, [checkOutDate]);

  React.useEffect(() => {
    if (
      quoteData?.bookingQuoteByListingAndDates.__typename === 'BookingQuote'
    ) {
      emitViewQuoteEvent(quoteData.bookingQuoteByListingAndDates);
    }
  }, [quoteData]);

  /**
   * Determines if a given date should be blocked off in the calendar based on the listing's availability.
   * Any date that is more than one year from today is blocked off.
   */
  const isDayBlocked = React.useCallback(
    (date: moment.Moment) => {
      if (date.isSameOrAfter(nextUnavailableDate, 'day')) {
        return true;
      } else if (checkInDate && date.isSameOrBefore(checkInDate, 'day')) {
        return true;
      } else {
        const availability = availabilityDates[date.format('YYYY-MM-DD')];
        if (
          !checkInDate &&
          (!availability?.available || !availability?.maxNights)
        ) {
          return true;
        } else if (
          checkInDate &&
          !checkOutDate &&
          availability?.checkOutAllowed
        ) {
          return false;
        } else {
          return !availability?.available;
        }
      }
    },
    [
      // NOTE - We need to stringify unavailableDates because it is an array of Date objects
      checkInDate ? checkInDate.toString() : '',
      checkOutDate ? checkOutDate.toString() : '',
      JSON.stringify(availabilityDates),
      nextUnavailableDate ? nextUnavailableDate.toString() : '',
    ]
  );

  const isStartDayBlocked = React.useCallback(
    (date: moment.Moment) => {
      if (!isInclusivelyAfterDay(date, currentMoment)) return true;
      if (checkInDate) return false;

      const availability = availabilityDates[date.format('YYYY-MM-DD')];

      const previousDay = date.clone().subtract(1, 'day').format('YYYY-MM-DD');

      const previousAvailability = availabilityDates[previousDay];

      if (
        !availability?.available &&
        availability?.checkOutAllowed &&
        previousAvailability?.available
      ) {
        return true;
      }

      if (
        availability?.maxNights === 0 &&
        availability?.available &&
        availability.checkOutAllowed
      ) {
        return true;
      }

      return !availability?.checkInAllowed && availability?.available;
    },
    [
      // NOTE - We need to stringify unavailableDates because it is an array of Date objects
      checkInDate ? checkInDate.toString() : '',
      JSON.stringify(availabilityDates),
    ]
  );

  const isEndDayBlocked = React.useCallback(
    (date: moment.Moment) => {
      if (!isInclusivelyAfterDay(date, currentMoment)) return true;

      const availability = availabilityDates[date.format('YYYY-MM-DD')];

      // Return false if there is no checkInDate
      if (!checkInDate) return false;

      // Return true if checkOutAllowed is false
      if (
        checkInDate &&
        availability &&
        availability.checkOutAllowed === false
      ) {
        return true;
      }

      return !availability?.checkOutAllowed && availability?.available;
    },
    [
      // NOTE - We need to stringify unavailableDates because it is an array of Date objects
      checkInDate ? checkInDate.toString() : '',
      JSON.stringify(availabilityDates),
    ]
  );

  /**
   * Get the minimum number of nights for a given date.
   */
  const getMinNightsForHoverDate = React.useCallback(
    (date: moment.Moment) => {
      const availability = availabilityDates[date.format('YYYY-MM-DD')];
      return availability?.minNights || 1;
    },
    [JSON.stringify(availabilityDates)]
  );

  const getNextUnavailableDate = (
    availabilityDates: AvailabilityHash,
    checkInDate: string
  ): string => {
    let nextUnavailableDate = formatDateYYYYMMDD(
      moment().add(1, 'year').toDate()
    );

    const availability = availabilityDates[checkInDate];
    if (availability && availability.maxNights && availability.maxNights > 0) {
      const maxNightDate = moment(checkInDate).add(
        availability.maxNights + 1,
        'days'
      );
      if (maxNightDate.isBefore(moment(nextUnavailableDate))) {
        nextUnavailableDate = formatDateYYYYMMDD(maxNightDate);
      }
    }

    return nextUnavailableDate;
  };

  const contextValue: QuotesContext = {
    activateQuotesModal,
    deactivateQuotesModal,
    quotesModalActive,
    adultCount,
    setAdultCount,
    childCount,
    setChildCount,
    infantCount,
    setInfantCount,
    petCount,
    setPetCount,
    guestCountHumanReadableValue,
    guestCapacity,
    guestCapacityReached,
    checkInDate,
    setCheckInDate,
    checkOutDate,
    setCheckOutDate,
    quoteData,
    quoteDataLoading,
    calculateServiceFeeSavingsCents,
    datesPresent,
    datesInvalid,
    numberOfNights,
    bookingQuotePresent,
    availabilityData,
    availabilityDataLoading,
    isDayBlocked,
    isStartDayBlocked,
    isEndDayBlocked,
    minimumNightsForSelectedDate,
    getMinNightsForHoverDate,
  };

  return (
    <QuotesContext.Provider value={contextValue}>
      {children}
    </QuotesContext.Provider>
  );
};

export const useQuotes = () => {
  const context = React.useContext(QuotesContext);

  if (context === undefined) {
    throw new Error('useQuotes must be used within a QuotesProvider');
  }

  return context;
};
